import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@connectrpc/connect-query";
import { useQueryClient } from "@tanstack/react-query";
import { GaugeIcon, Loader2Icon, RotateCcwIcon, ShieldAlertIcon } from "lucide-react";
import { toast } from "sonner";
import { create } from "@bufbuild/protobuf";

import { ErrorState, FieldError } from "@/components/error-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  AdminService,
  SendPolicySchema,
  type Account,
  type SendPolicy,
} from "@/gen/wabms/admin/v1/admin_pb";
import { invalidateAccounts } from "@/lib/admin-queries";
import { errorMessage, unclaimedViolations, violationFor } from "@/lib/errors";
import { formatBoth, formatCount } from "@/lib/format";
import { cn } from "@/lib/utils";

/** Field paths this form owns, matched against ErrorDetail.field_violations. */
const POLICY_FIELDS = [
  "msgs_per_minute",
  "min_gap_ms",
  "jitter_ms",
  "daily_cap",
  "paused",
  "paused_reason",
] as const;

/**
 * Conservative ceilings for a *warmed up* companion device. These are not
 * WhatsApp-published numbers — no such numbers exist for the unofficial
 * multi-device protocol — they are the thresholds above which operators
 * consistently report bans. The UI warns rather than blocks: the operator owns
 * the risk, but they must not be able to say they were not told.
 */
const SAFE_MSGS_PER_MINUTE = 15;
const SAFE_MIN_GAP_MS = 3000;
const SAFE_DAILY_CAP = 1000;
const MIN_RECOMMENDED_JITTER_MS = 1000;

interface Draft {
  msgsPerMinute: number;
  minGapMs: number;
  jitterMs: number;
  dailyCap: number;
  paused: boolean;
  pausedReason: string;
}

function toDraft(policy: SendPolicy | undefined): Draft {
  return {
    msgsPerMinute: policy?.msgsPerMinute ?? 10,
    minGapMs: policy?.minGapMs ?? 5000,
    jitterMs: policy?.jitterMs ?? 2000,
    dailyCap: policy?.dailyCap ?? 500,
    paused: policy?.paused ?? false,
    pausedReason: policy?.pausedReason ?? "",
  };
}

interface Risk {
  level: "ok" | "warn" | "danger";
  findings: string[];
}

function assess(draft: Draft): Risk {
  const findings: string[] = [];
  let danger = false;

  if (draft.msgsPerMinute > SAFE_MSGS_PER_MINUTE) {
    findings.push(
      `${draft.msgsPerMinute} messages/minute is above the ${SAFE_MSGS_PER_MINUTE}/min that a warmed-up device sustains safely.`,
    );
    if (draft.msgsPerMinute > SAFE_MSGS_PER_MINUTE * 2) danger = true;
  }
  if (draft.minGapMs < SAFE_MIN_GAP_MS) {
    findings.push(
      `A ${draft.minGapMs} ms minimum gap sends faster than a human can type. Keep it at or above ${SAFE_MIN_GAP_MS} ms.`,
    );
    if (draft.minGapMs < SAFE_MIN_GAP_MS / 3) danger = true;
  }
  if (draft.jitterMs < MIN_RECOMMENDED_JITTER_MS) {
    findings.push(
      "Little or no jitter makes the send pattern perfectly periodic, which is the easiest thing for spam detection to spot.",
    );
  }
  if (draft.dailyCap > SAFE_DAILY_CAP) {
    findings.push(
      `A daily cap of ${formatCount(draft.dailyCap)} is beyond what most unofficial-API numbers survive. Raise it gradually over weeks.`,
    );
    if (draft.dailyCap > SAFE_DAILY_CAP * 3) danger = true;
  }

  if (findings.length === 0) return { level: "ok", findings };
  return { level: danger ? "danger" : "warn", findings };
}

/**
 * Effective throughput is whichever of the two pacing controls binds first: the
 * rate limit, or the minimum gap plus half the average jitter.
 */
function derivedPerHour(draft: Draft): { perHour: number; boundBy: "rate" | "gap" } {
  const fromRate = draft.msgsPerMinute * 60;
  const effectiveGapMs = draft.minGapMs + draft.jitterMs / 2;
  const fromGap = effectiveGapMs > 0 ? Math.floor(3_600_000 / effectiveGapMs) : fromRate;
  return fromGap < fromRate
    ? { perHour: fromGap, boundBy: "gap" }
    : { perHour: fromRate, boundBy: "rate" };
}

export function SendPolicyTab({ account }: { account: Account }) {
  const queryClient = useQueryClient();
  const initial = useMemo(() => toDraft(account.sendPolicy), [account.sendPolicy]);
  const [draft, setDraft] = useState<Draft>(initial);
  const [error, setError] = useState<unknown>(undefined);

  // A successful save (or a refetch triggered elsewhere) rebases the form on
  // whatever the server now says the policy is.
  useEffect(() => setDraft(initial), [initial]);

  const updatePolicy = useMutation(AdminService.method.updateSendPolicy);

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const risk = assess(draft);
  const { perHour, boundBy } = derivedPerHour(draft);
  const hoursToCap = perHour > 0 ? draft.dailyCap / perHour : 0;

  const violations = {
    msgsPerMinute: violationFor(error, "msgs_per_minute"),
    minGapMs: violationFor(error, "min_gap_ms"),
    jitterMs: violationFor(error, "jitter_ms"),
    dailyCap: violationFor(error, "daily_cap"),
    pausedReason: violationFor(error, "paused_reason"),
  };
  const otherViolations = unclaimedViolations(error, POLICY_FIELDS);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  async function save() {
    setError(undefined);
    try {
      await updatePolicy.mutateAsync({
        accountId: account.accountId,
        sendPolicy: create(SendPolicySchema, {
          msgsPerMinute: draft.msgsPerMinute,
          minGapMs: draft.minGapMs,
          jitterMs: draft.jitterMs,
          dailyCap: draft.dailyCap,
          paused: draft.paused,
          pausedReason: draft.pausedReason,
          // warmup_started_at is server-owned; echo it back untouched.
          warmupStartedAt: account.sendPolicy?.warmupStartedAt,
        }),
      });
      await invalidateAccounts(queryClient);
      toast.success("Send policy saved", {
        description: `Effective throughput is now about ${formatCount(perHour)} messages/hour.`,
      });
    } catch (err) {
      setError(err);
      toast.error("Send policy not saved", { description: errorMessage(err) });
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Pacing</CardTitle>
            <CardDescription>
              This pacing is the only thing standing between the account and a WhatsApp ban. A
              banned number cannot be recovered — you lose the number, the linked device history
              and everything queued behind it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <NumberField
              id="msgs-per-minute"
              label="Messages per minute"
              help="Hard ceiling on send rate, evaluated over a sliding minute."
              value={draft.msgsPerMinute}
              min={1}
              max={120}
              step={1}
              unsafeAbove={SAFE_MSGS_PER_MINUTE}
              violation={violations.msgsPerMinute}
              onChange={(v) => set("msgsPerMinute", v)}
            />

            <NumberField
              id="min-gap"
              label="Minimum gap"
              suffix="ms"
              help="Never send two messages closer together than this, regardless of the rate above."
              value={draft.minGapMs}
              min={0}
              max={30_000}
              step={100}
              unsafeBelow={SAFE_MIN_GAP_MS}
              violation={violations.minGapMs}
              onChange={(v) => set("minGapMs", v)}
            />

            <NumberField
              id="jitter"
              label="Jitter"
              suffix="ms"
              help="Random extra delay, 0…jitter, added to every gap so the pattern is not periodic."
              value={draft.jitterMs}
              min={0}
              max={30_000}
              step={100}
              unsafeBelow={MIN_RECOMMENDED_JITTER_MS}
              violation={violations.jitterMs}
              onChange={(v) => set("jitterMs", v)}
            />

            <NumberField
              id="daily-cap"
              label="Daily cap"
              help="Sending stops for the day once this many messages have reached SENT."
              value={draft.dailyCap}
              min={0}
              max={10_000}
              step={50}
              unsafeAbove={SAFE_DAILY_CAP}
              violation={violations.dailyCap}
              onChange={(v) => set("dailyCap", v)}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pause</CardTitle>
            <CardDescription>
              A paused account keeps accepting batches and queueing messages; it simply stops
              handing them to WhatsApp.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="paused">Pause sending</Label>
                <p className="text-xs text-muted-foreground">
                  {account.queueDepth > 0
                    ? `${formatCount(account.queueDepth)} message${account.queueDepth === 1 ? "" : "s"} are currently queued.`
                    : "Nothing is queued right now."}
                </p>
              </div>
              <Switch
                id="paused"
                checked={draft.paused}
                onCheckedChange={(v) => set("paused", v)}
              />
            </div>

            {draft.paused ? (
              <div className="space-y-2">
                <Label htmlFor="paused-reason">Reason</Label>
                <Input
                  id="paused-reason"
                  placeholder="Investigating delivery failures"
                  value={draft.pausedReason}
                  aria-invalid={violations.pausedReason !== undefined}
                  onChange={(e) => set("pausedReason", e.target.value)}
                />
                <FieldError message={violations.pausedReason} />
                <p className="text-xs text-muted-foreground">
                  Shown on the accounts grid so the next operator knows why.
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {error !== undefined && otherViolations.length > 0 ? (
          <ErrorState error={error} title="The service rejected this policy" />
        ) : error !== undefined && !Object.values(violations).some(Boolean) ? (
          <ErrorState error={error} title="Send policy not saved" />
        ) : null}

        <div className="flex items-center gap-2">
          <Button disabled={!dirty || updatePolicy.isPending} onClick={() => void save()}>
            {updatePolicy.isPending ? <Loader2Icon className="animate-spin" /> : null}
            Save send policy
          </Button>
          <Button
            variant="ghost"
            disabled={!dirty || updatePolicy.isPending}
            onClick={() => {
              setDraft(initial);
              setError(undefined);
            }}
          >
            <RotateCcwIcon /> Reset
          </Button>
          {dirty ? <span className="text-xs text-muted-foreground">Unsaved changes</span> : null}
        </div>
      </div>

      <div className="space-y-4">
        <Card className="sticky top-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <GaugeIcon className="size-4" /> Derived throughput
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <p className="tabular text-3xl leading-none font-semibold">
                ≈ {formatCount(perHour)}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                messages/hour, bound by the{" "}
                {boundBy === "gap" ? "minimum gap and jitter" : "per-minute rate"}.
              </p>
            </div>

            <Separator />

            <dl className="space-y-1.5 text-xs">
              <Stat label="Average gap">
                {formatCount(draft.minGapMs + Math.round(draft.jitterMs / 2))} ms
              </Stat>
              <Stat label="Daily cap reached in">
                {draft.dailyCap > 0 && perHour > 0
                  ? `${hoursToCap.toFixed(1)} h of continuous sending`
                  : "—"}
              </Stat>
              <Stat label="Sent today">
                {formatCount(account.sentToday)} / {formatCount(draft.dailyCap)}
              </Stat>
              <Stat label="Warm-up started">
                {account.sendPolicy?.warmupStartedAt
                  ? formatBoth(account.sendPolicy.warmupStartedAt)
                  : "not started"}
              </Stat>
            </dl>

            <Separator />

            {risk.level === "ok" ? (
              <Alert className="border-tone-success/40 bg-tone-success/5">
                <ShieldAlertIcon className="text-tone-success" />
                <AlertTitle className="text-xs">Within conservative limits</AlertTitle>
                <AlertDescription className="text-xs">
                  These settings look survivable for a warmed-up number. Watch the failure rate
                  after every change.
                </AlertDescription>
              </Alert>
            ) : (
              <Alert
                variant="destructive"
                className={cn(
                  risk.level === "warn" && "border-tone-warning/40 bg-tone-warning/5 text-tone-warning",
                  risk.level === "danger" && "border-tone-danger/50 bg-tone-danger/5",
                )}
              >
                <ShieldAlertIcon />
                <AlertTitle className="text-xs">
                  {risk.level === "danger"
                    ? "These settings will very likely get this number banned"
                    : "These settings raise the ban risk"}
                </AlertTitle>
                <AlertDescription className="text-xs">
                  <ul className="mt-1 list-outside list-disc space-y-1 pl-4">
                    {risk.findings.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  <p className="mt-2">
                    A ban is permanent for that phone number. There is no appeal path for
                    unofficial multi-device clients.
                  </p>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tabular text-right font-medium">{children}</dd>
    </div>
  );
}

function NumberField({
  id,
  label,
  help,
  suffix,
  value,
  min,
  max,
  step,
  unsafeAbove,
  unsafeBelow,
  violation,
  onChange,
}: {
  id: string;
  label: string;
  help: string;
  suffix?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unsafeAbove?: number;
  unsafeBelow?: number;
  /** Server-side rejection for this exact field, if the last save produced one. */
  violation?: string;
  onChange: (value: number) => void;
}) {
  const unsafe =
    (unsafeAbove !== undefined && value > unsafeAbove) ||
    (unsafeBelow !== undefined && value < unsafeBelow);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={id}>{label}</Label>
        <div className="flex items-center gap-1.5">
          <Input
            id={id}
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            step={step}
            aria-invalid={unsafe || violation !== undefined}
            className={cn(
              "tabular h-8 w-28 text-right",
              unsafe && "border-tone-warning",
              violation !== undefined && "border-destructive",
            )}
            value={value}
            onChange={(e) => {
              const next = Number.parseInt(e.target.value, 10);
              onChange(Number.isFinite(next) ? Math.min(max, Math.max(min, next)) : min);
            }}
          />
          {suffix ? <span className="w-6 text-xs text-muted-foreground">{suffix}</span> : null}
        </div>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        aria-label={label}
        onValueChange={([v]) => onChange(v ?? min)}
      />
      <FieldError message={violation} />
      <p className={cn("text-xs", unsafe ? "text-tone-warning" : "text-muted-foreground")}>{help}</p>
    </div>
  );
}
